import { useState } from 'react';
import moment from 'moment';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  type SignUpStep3Schema as CreditCardFormSchema,
  signUpStep3Schema as creditCardFormSchema,
} from 'blocks/sign-up/schemas';
import { zodResolver } from '@hookform/resolvers/zod';

import { Heading } from 'components/text';
import Button from 'components/Button/BaseButton';
import Breadcrumbs from 'components/breadcrumbs';

import Lock from 'assets/svg/Lock.svg';

import Summary from './summary';
import CreditCardForm from './credit-card-form/CreditCardForm';
import TermsAndConditions from './terms-and-conditions';
import ConfirmationModal from '../payment-confirmation/Modal';

import styles from './Payment.module.scss';

import { createVouchers } from 'api/vouchers';
import { useVoucherContext } from '../context';

import { currencyHelper } from 'utils/currencyHelper';
import { getErrorMsgObj } from 'utils/serverValidationErrors';
import { getRequestObject } from './utils';

import type { ErrorMessages } from 'types/types';
import { useRouter } from 'next/router';
import StripeService from 'services/StripeService/StripeService';

const Payment = () => {
  const methods = useForm<CreditCardFormSchema>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      creditCard: {
        brand: '',
        number: '',
        expiry: '',
        cvc: '',
        name: '',
      },
    },
    resolver: zodResolver(creditCardFormSchema),
  });
  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = methods;

  const { state, resetState } = useVoucherContext();

  const { vouchers } = state;
  const [
    {
      amount,
      creator_email,
      creator_name,
      message,
      recipient_email,
      recipient_name,
      send_date,
      currency,
      send_date_option,
      custom_gift_card_id,
    },
  ] = vouchers;

  const formattedSendDate = moment(send_date).format('DD/MM/YYYY');

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const router = useRouter();

  const { isLoading, mutateAsync } = useMutation(createVouchers, {
    onError: (error: ErrorMessages | any) => {
      const errorMsgObj = getErrorMsgObj(error?.response?.data);
      if (Object.keys(errorMsgObj.credit_card).length === 0) {
        return;
      }

      const [field, message] = Object.entries(errorMsgObj.credit_card)[0];

      setError(
        `creditCard.${field as keyof CreditCardFormSchema['creditCard']}`,
        {
          type: 'manual',
          message: message as string,
        },
        { shouldFocus: true }
      );
    },
  });

  const handleCloseModal = () => {
    setIsConfirmationModalOpen(false);
    router.replace('/e-gift-card');

    resetState();
  };

  const onSubmit: SubmitHandler<CreditCardFormSchema> = async data => {
    const requestObj = getRequestObject({ ...state, ...data });

    const res = await mutateAsync(requestObj);

    if (res.payment.requires_action) {
      try {
        const clientSecret = res.payment.client_secret;
        await StripeService.shared().handleCardAction(clientSecret);
      } catch (err: any) {
        setError('creditCard', {
          type: 'manual',
          message:
            err?.message ||
            'An error occurred while processing your card. Please try again.',
        });
        return;
      }
    }

    setIsConfirmationModalOpen(true);
  };

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <Heading as="h2" className={styles.titleMobile}>
          Payment
        </Heading>
        <Summary
          sender={{ email: creator_email, name: creator_name }}
          receiver={{
            email: recipient_email,
            name: recipient_name,
          }}
          sendDate={formattedSendDate}
          message={message}
          amount={currencyHelper(Number(amount.replace(',', '')), currency)}
          customGiftCardId={custom_gift_card_id}
        />
        <div className={styles.creditCard}>
          <Heading as="h2" className={styles.titleDesktop}>
            Payment
          </Heading>
          <CreditCardForm />
          <TermsAndConditions />
          <Button isLoading={isLoading} className={styles.payNow}>
            <Lock />
            Pay now
          </Button>
        </div>
      </form>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCloseModal}
        shouldSendLater={send_date_option === 'later'}
      />
    </FormProvider>
  );
};

export default Payment;
